import {
  compareDecimals,
  type CompiledFieldNode,
  type CompiledForm,
  type FieldValue,
  type FormPermissionsStructure,
  type FormRulesStructure,
} from '@record-fill';
import { ruleEffects, type RuleAction, type RuleEffect } from './rule-trace';
import type { FieldOption, LabGrant, LabRule } from './useMatrixFillLab';

export interface DemoSubject {
  id: number;
  name: string;
}

export interface DemoLab {
  fieldOptions: FieldOption[];
  storedRules: FormRulesStructure | null;
  storedPermissions: FormPermissionsStructure | null;
  anchorFieldIds: string[];
  positions: DemoSubject[];
  functionalActivities: DemoSubject[];
  resetLab(): void;
  setValue(fieldId: string, value: FieldValue): void;
  locateField(fieldId: string): void;
  selectSubject(positionIds: number[], functionalActivityIds: number[]): void;
  compiled(): CompiledForm | null;
  sampleValue(fieldId: string, variant: number): FieldValue;
  saveResponse(): void;
  tryArchive(): void;
  openReport(): void;
}

export interface DemoStep {
  id: string;
  title: string;
  description: string;
  run(): void;
}

interface SubjectPick {
  positionIds: number[];
  functionalActivityIds: number[];
}

interface GrantGroup extends SubjectPick {
  key: string;
  fieldIds: string[];
}

const ACTION_VERB: Record<RuleAction, string> = { show: 'mostra', hide: 'oculta', disable: 'desabilita' };
const TEXT_CANDIDATES = ['01/02/2026', '12.345.678/0001-90', '08:30', '2026-02-01', '12345', 'A1'];
const MAX_FILL_PASSES = 4;
const KEY_SEPARATOR = '::';

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} e ${names.at(-1)}`;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function subjectName(subjects: DemoSubject[], id: number, fallback: string): string {
  return subjects.find((subject) => subject.id === id)?.name ?? `${fallback} #${id}`;
}

function groupKey(grant: SubjectPick): string {
  const sorted = (ids: number[]) => [...ids].sort((a, b) => a - b).join(',');
  return `${sorted(grant.positionIds)}|${sorted(grant.functionalActivityIds)}`;
}

function grantGroups(lab: DemoLab): GrantGroup[] {
  const order = new Map(lab.fieldOptions.map((field, index) => [field.id, index]));
  const groups = new Map<string, GrantGroup>();
  const grants: LabGrant[] = [...(lab.storedPermissions?.fields ?? [])].sort(
    (a, b) => (order.get(a.fieldId) ?? Infinity) - (order.get(b.fieldId) ?? Infinity),
  );
  for (const grant of grants) {
    if (grant.positionIds.length === 0 && grant.functionalActivityIds.length === 0) continue;
    const key = groupKey(grant);
    const group = groups.get(key) ?? {
      key,
      positionIds: grant.positionIds,
      functionalActivityIds: grant.functionalActivityIds,
      fieldIds: [],
    };
    group.fieldIds.push(grant.fieldId);
    groups.set(key, group);
  }
  return [...groups.values()];
}

function firstSubjectOf(group: SubjectPick): SubjectPick {
  return group.positionIds.length > 0
    ? { positionIds: [group.positionIds[0]], functionalActivityIds: [] }
    : { positionIds: [], functionalActivityIds: [group.functionalActivityIds[0]] };
}

function firstSubjectName(lab: DemoLab, group: SubjectPick): string {
  return group.positionIds.length > 0
    ? subjectName(lab.positions, group.positionIds[0], 'cargo')
    : subjectName(lab.functionalActivities, group.functionalActivityIds[0], 'atividade');
}

function subjectWithoutGrants(lab: DemoLab): DemoSubject | null {
  const granted = new Set((lab.storedPermissions?.fields ?? []).flatMap((grant) => grant.positionIds));
  return lab.positions.find((position) => !granted.has(position.id)) ?? null;
}

function outsiderPick(lab: DemoLab): SubjectPick {
  const outsider = subjectWithoutGrants(lab);
  return { positionIds: outsider ? [outsider.id] : [], functionalActivityIds: [] };
}

function triggerValue(field: FieldOption, option: string): FieldValue {
  return field.kind === 'checkbox' ? [option] : option;
}

function describeEffects(lab: DemoLab, effects: RuleEffect[]): string {
  const labels = new Map(lab.fieldOptions.map((field) => [field.id, field.label]));
  return effects
    .map((effect) => {
      const fieldNames = effect.targetFieldIds.flatMap((id) => labels.get(id) ?? []);
      const hasStatic = effect.targetFieldIds.some((id) => !labels.has(id));
      const verb = ACTION_VERB[effect.action];
      if (fieldNames.length === 0) return `${verb} o bloco`;
      const fields = fieldNames.length === 1 ? `o campo ${fieldNames[0]}` : `os campos ${joinNames(fieldNames)}`;
      return hasStatic ? `${verb} o bloco com ${fields}` : `${verb} ${fields}`;
    })
    .join('; ');
}

function ruleSteps(lab: DemoLab): DemoStep[] {
  const fieldIndex = new Map(lab.fieldOptions.map((field, index) => [field.id, index]));
  const grantOf = new Map((lab.storedPermissions?.fields ?? []).map((grant) => [grant.fieldId, grant]));
  const byTrigger = new Map<string, LabRule[]>();
  for (const rule of lab.storedRules?.rules ?? []) {
    if (!fieldIndex.has(rule.triggerFieldId)) continue;
    const key = `${rule.triggerFieldId}${KEY_SEPARATOR}${rule.triggerOption}`;
    byTrigger.set(key, [...(byTrigger.get(key) ?? []), rule]);
  }

  return [...byTrigger.entries()]
    .sort(([a], [b]) => (fieldIndex.get(a.split(KEY_SEPARATOR)[0]) ?? 0) - (fieldIndex.get(b.split(KEY_SEPARATOR)[0]) ?? 0))
    .map(([key, group]) => {
      const triggerId = group[0].triggerFieldId;
      const option = group[0].triggerOption;
      const trigger = lab.fieldOptions[fieldIndex.get(triggerId) as number];
      const effects = group.flatMap(ruleEffects);
      const firstTarget = effects.flatMap((effect) => effect.targetFieldIds).find((id) => fieldIndex.has(id));
      const guardian = grantOf.get(triggerId);
      const guardianNote = guardian
        ? ` O gatilho fica em um bloco protegido, então o roteiro entra como ${firstSubjectName(lab, guardian)}.`
        : '';
      return {
        id: `rule:${key}`,
        title: `Escolher ${trigger.label} = '${option}'`,
        description: `${capitalize(describeEffects(lab, effects))}.${guardianNote}`,
        run: () => {
          if (guardian && lab.compiled()?.fields[triggerId]?.state.editable === false) {
            const subject = firstSubjectOf(guardian);
            lab.selectSubject(subject.positionIds, subject.functionalActivityIds);
          }
          lab.setValue(triggerId, triggerValue(trigger, option));
          lab.locateField(firstTarget ?? triggerId);
        },
      };
    });
}

function groupAudience(lab: DemoLab, group: GrantGroup): string {
  const positions = group.positionIds.map((id) => subjectName(lab.positions, id, 'cargo'));
  const activities = group.functionalActivityIds.map((id) => subjectName(lab.functionalActivities, id, 'atividade'));
  return joinNames(activities.length > 0 ? [...positions, `a atividade ${joinNames(activities)}`] : positions);
}

function permissionSteps(lab: DemoLab, groups: GrantGroup[]): DemoStep[] {
  if (groups.length === 0) return [];
  const labels = new Map(lab.fieldOptions.map((field) => [field.id, field.label]));
  const steps: DemoStep[] = groups.map((group) => ({
    id: `grant:${group.key}`,
    title: `Entrar como ${firstSubjectName(lab, group)}`,
    description: `Os campos ${joinNames(group.fieldIds.map((id) => labels.get(id) ?? id))} aparecem só para ${groupAudience(lab, group)}.`,
    run: () => {
      const subject = firstSubjectOf(group);
      lab.selectSubject(subject.positionIds, subject.functionalActivityIds);
      lab.locateField(group.fieldIds[0]);
    },
  }));

  const outsider = subjectWithoutGrants(lab);
  steps.push({
    id: 'grant:none',
    title: 'Voltar a um cargo sem permissão',
    description: outsider
      ? `Os blocos protegidos desaparecem porque ${outsider.name} não está em nenhuma permissão.`
      : 'Os blocos protegidos desaparecem porque ninguém está selecionado.',
    run: () => {
      const pick = outsiderPick(lab);
      lab.selectSubject(pick.positionIds, pick.functionalActivityIds);
    },
  });
  return steps;
}

function isEmpty(value: FieldValue): boolean {
  return value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

function valueFor(lab: DemoLab, field: CompiledFieldNode, variant: number): FieldValue {
  const control = field.control;
  if (control.kind === 'number') {
    const candidate = String(variant);
    if (control.min !== undefined && compareDecimals(candidate, control.min) < 0) return control.min;
    if (control.max !== undefined && compareDecimals(candidate, control.max) > 0) return control.max;
    return candidate;
  }
  if (control.kind === 'text' && control.regex) {
    const pattern = new RegExp(control.regex);
    return TEXT_CANDIDATES.find((candidate) => pattern.test(candidate)) ?? lab.sampleValue(field.id, variant);
  }
  return lab.sampleValue(field.id, variant);
}

function emptyValueFor(field: CompiledFieldNode): FieldValue {
  if (field.control.kind === 'checkbox') return [];
  if (field.control.kind === 'number') return null;
  return '';
}

// Filling a trigger can reveal more fields, so the pass repeats until nothing new appears.
export function fillEditableFields(lab: DemoLab, variant: number, except: Set<string> = new Set()): void {
  for (let pass = 0; pass < MAX_FILL_PASSES; pass += 1) {
    const form = lab.compiled();
    if (!form) return;
    let touched = false;
    for (const fieldId of form.fieldOrder) {
      const field = form.fields[fieldId];
      const { visible, editable, enabled } = field.state;
      if (except.has(fieldId) || !visible || !editable || !enabled || !isEmpty(field.value)) continue;
      lab.setValue(fieldId, valueFor(lab, field, variant));
      touched = true;
    }
    if (!touched) return;
  }
}

// The first anchor in matrix order is the one the script leaves empty, so the audience sees
// the error land on a field they already know.
function archiveSteps(lab: DemoLab): DemoStep[] {
  const order = new Map(lab.fieldOptions.map((field, index) => [field.id, index]));
  const anchors = lab.anchorFieldIds.filter((id) => order.has(id)).sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  const anchorId = anchors[0];
  if (!anchorId) return [];
  const label = lab.fieldOptions[order.get(anchorId) as number].label;
  const names = anchors.map((id) => lab.fieldOptions[order.get(id) as number].label);
  return [
    {
      id: 'archive:blocked',
      title: 'Tentar arquivar com âncora vazio',
      description: `Preenche o formulário inteiro menos ${label} e tenta arquivar: o campo âncora vazio recebe o erro na própria folha. Âncoras desta matriz: ${joinNames(names)}.`,
      run: () => {
        const field = lab.compiled()?.fields[anchorId];
        if (field && !isEmpty(field.value)) lab.setValue(anchorId, emptyValueFor(field));
        fillEditableFields(lab, 1, new Set([anchorId]));
        lab.tryArchive();
        lab.locateField(anchorId);
      },
    },
    {
      id: 'archive:ready',
      title: 'Preencher o âncora e arquivar',
      description: `Preenche ${label} e tenta de novo: o erro some e o painel mostra "Pronto para arquivar".`,
      run: () => {
        fillEditableFields(lab, 1);
        lab.tryArchive();
        lab.locateField(anchorId);
      },
    },
  ];
}

function reportStep(lab: DemoLab, groups: GrantGroup[]): DemoStep {
  const outsiderName = subjectWithoutGrants(lab)?.name ?? 'um cargo sem permissão';
  const names = [...groups.map((group) => firstSubjectName(lab, group)), outsiderName];
  return {
    id: 'report',
    title: 'Salvar respostas e gerar o relatório',
    description: `Salva uma resposta preenchida como ${joinNames(names)} e abre o relatório como ${outsiderName}: as colunas protegidas não existem para quem pede.`,
    run: () => {
      const picks = [...groups.map(firstSubjectOf), outsiderPick(lab)];
      picks.forEach((pick, index) => {
        lab.selectSubject(pick.positionIds, pick.functionalActivityIds);
        fillEditableFields(lab, index + 1);
        lab.saveResponse();
      });
      const outsider = outsiderPick(lab);
      lab.selectSubject(outsider.positionIds, outsider.functionalActivityIds);
      lab.openReport();
    },
  };
}

export function buildDemoScript(lab: DemoLab): DemoStep[] {
  const rules = lab.storedRules?.rules ?? [];
  const groups = grantGroups(lab);
  if (rules.length === 0 && groups.length === 0 && lab.anchorFieldIds.length === 0) return [];

  const hasShowRules = rules.some((rule) => ruleEffects(rule).some((effect) => effect.action === 'show'));
  const intro = [
    hasShowRules ? 'blocos alvo de regras "mostrar" começam ocultos' : null,
    groups.length > 0 ? 'blocos protegidos por permissão não aparecem sem um cargo' : null,
  ].filter((part): part is string => part !== null);

  return [
    {
      id: 'reset',
      title: 'Formulário limpo',
      description: `${capitalize(intro.length > 0 ? intro.join(' e ') : 'o formulário volta ao estado inicial')}.`,
      run: () => lab.resetLab(),
    },
    ...ruleSteps(lab),
    ...permissionSteps(lab, groups),
    ...archiveSteps(lab),
    reportStep(lab, groups),
  ];
}
